<!-- 
  INFO:
    Stepper displays a vertical list of numbered navigation steps, always with a current
    step highlighted. It is designed to be used with other paged UI content. 
    
    Steps become interactive if the @select event is bound. Each step can be disabled
    individually if necessary. Steps can also be displayed in a green 'complete' state,
    or in a red 'invalid' state.

    Font family and size will be inherited from the page. There are no dependencies other
    than Vue and SCSS.

  PROPS:
    currentStep: Number of currently selected step (starting at 1 not 0)

    steps: Array of step names and optional status values (complete/invalid/disabled), e.g.
      [
        { name: 'Example step 1...' },
        { name: 'Example step 2...', status: 'complete' },
        { name: 'Example step 3...', status: 'invalid' },
        { name: 'Example step 4...', status: 'disabled' },
      ]

    stepSpacing: (Optional) Vertical spacing between steps, e.g "10px" or "1.5em" etc. Default is "1em"

  EVENTS:
    @select(stepNumber) - Makes steps clickable and returns the clicked step number. This will not actually change the current step because it is controlled by the parent component (through the currentStep property). 

-->

<template>
  <nav class="ads-stepper" role="navigation">
    <ol>
      <li v-for="(step, idx) in steps" :key="step.name">
        <button
          :class="getStepCssClasses(idx)"
          type="button"
          @click="selectStep(idx)"
          :disabled="
            !isInteractive || step.status === 'disabled' || isSelected(idx)
          "
        >
          <span class="step-circle">
            <svg
              v-if="step.status === 'complete'"
              aria-hidden="true"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path
                fill="currentColor"
                d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
              ></path>
            </svg>
            <svg
              v-else-if="step.status === 'invalid'"
              aria-hidden="true"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 192 512"
            >
              <path
                fill="currentColor"
                d="M176 432c0 44.112-35.888 80-80 80s-80-35.888-80-80 35.888-80 80-80 80 35.888 80 80zM25.26 25.199l13.6 272C39.499 309.972 50.041 320 62.83 320h66.34c12.789 0 23.331-10.028 23.97-22.801l13.6-272C167.425 11.49 156.496 0 142.77 0H49.23C35.504 0 24.575 11.49 25.26 25.199z"
              ></path>
            </svg>
            <span v-else>{{ idx + 1 }}</span>
          </span>
          <span class="step-name">{{ step.name }}</span>
        </button>
        <div
          v-if="idx + 1 < steps.length"
          class="line"
          :style="`height:${stepSpacing}`"
        ></div>
      </li>
    </ol>
  </nav>
</template>

<script>
export default {
  name: 'Stepper',
  props: {
    steps: {
      default() {
        return [{ name: 'Example step 1...' }]
      },
      type: Array
    },
    currentStep: {
      default() {
        return 1
      },
      type: Number
    },
    stepSpacing: {
      default() {
        return '1em'
      },
      type: String
    }
  },

  computed: {
    isInteractive() {
      // If a @select event has been bound, make the control interactive
      return this.$listeners.select ? true : false
    }
  },

  methods: {
    isSelected(stepIdx) {
      return stepIdx === this.currentStep - 1
    },
    getStepCssClasses(stepIdx) {
      let classes = ['step']
      let step = this.steps[stepIdx]
      if (this.isSelected(stepIdx)) {
        classes.push('selected')
      }
      if (step.status) {
        classes.push(step.status)
      }
      return classes.join(' ')
    },
    selectStep(stepIdx) {
      this.$emit('select', stepIdx + 1)
    }
  }
}
</script>

<style lang="scss" scoped>
$ads-navy: #041e42; // Local copy of $ads-navy so this component can be reused elsewhere without dependencies
$border-width: 0.15em; // Used for circle border width and connecting line widths

.ads-stepper {
  color: $ads-navy;
  white-space: nowrap;
  line-height: 1;
}

.ads-stepper ol {
  list-style: none;
  padding: $border-width * 2; // Makes room for the selected step which expands slightly into the space around it
}

.step {
  opacity: 1;
  text-align: left;
  width: 100%;
  font-weight: bold;
  overflow: hidden;
  border-radius: 2em;

  // Remove default button styles (as each step is a button)
  background-color: transparent;
  padding: 0;
  margin: 0;
  border: 0;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  line-height: 1;

  &.selected {
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    padding: $border-width * 2; // Used to slightly expand selected step into the space around it
    margin: -$border-width * 2; // Used to counteract above padding, so other elements do not shift
  }

  &:focus {
    outline: none; // Hides default focus ring
    background-color: rgba(
      128,
      128,
      128,
      0.2
    ); // Shades background when focused
  }

  &:disabled {
    cursor: default;
  }
}

.step-circle {
  border: $border-width solid $ads-navy;
  border-radius: 50%;
  display: inline-block;
  background-color: white;
  margin-right: 0.5em;
  font-size: 1.25em;
  text-align: center;

  svg {
    padding: 0.5em;
  }
}

.selected .step-circle {
  color: white;
  background-color: $ads-navy;
}
.complete .step-circle {
  color: white;
  background-color: #30b400;
  border-color: #289b00;
}
.invalid .step-circle {
  color: #cd0337;
  border-color: #cd0337;
  background-color: white;
}

.step-circle > * {
  display: inline-block;
  width: 2em;
  height: 2em;
  line-height: 2em !important;
  font-weight: bold;
  vertical-align: middle;
}

.step-circle,
.step-name {
  vertical-align: middle;
  display: inline-block;
}

.line {
  // Our div is the exact width of a step circle
  font-size: 1.26em;
  height: 1em;
  width: 2em;
  margin: 0 $border-width;
  text-align: center;
  position: relative; // Allows line to sit on top of any overlapping selected step

  // Then we center a vertical line in the div
  &::before {
    border-right: $border-width solid;
    height: 100%;
    content: ' ';
    display: inline-block;
    vertical-align: top;
  }
}
</style>
